import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  Database,
  Download,
  Eye,
  FileCheck2,
  FileText,
  FolderOpen,
  Megaphone,
  Search,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import api from '../services/api';

const buildEmptyForm = (category = '') => ({
  category,
  name: '',
  date: '',
  thumbnail: '',
  year: ''
});

const Dossiers = () => {
  const userRole = localStorage.getItem('userRole') || '';

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFilePath, setUploadedFilePath] = useState(null);

  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [uploadedThumbnailPath, setUploadedThumbnailPath] = useState(null);

  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(userRole === 'state' ? 'training-modules' : 'mom');
  const [uploadForm, setUploadForm] = useState(buildEmptyForm());

  const tabs = userRole === 'state'
    ? [
        {
          id: 'training-modules',
          label: 'Training Modules',
          category: 'Training Modules',
          icon: FileCheck2
        }
      ]
    : [
        {
          id: 'mom',
          label: 'Minutes of Meeting (MoM)',
          category: 'MoM',
          icon: FileText
        },
        {
          id: 'tender',
          label: 'Recruitment/Tender',
          category: 'Tender',
          icon: Megaphone
        },
        {
          id: 'inspection',
          label: 'Inspection Notes',
          category: 'Inspection Notes',
          icon: FileCheck2
        },
        {
          id: 'review',
          label: 'Review & Training Presentation',
          category: 'Review meeting & Training particulars  presentation',
          icon: FileText
        },
        {
          id: 'acts-rules',
          label: 'Acts, Rules & GOs',
          category: 'Acts, Rules & GOs',
          icon: FileCheck2
        },
        {
          id: 'training-material',
          label: 'Training Material',
          category: 'Training Material',
          icon: FolderOpen
        },
        {
          id: 'database',
          label: 'Database',
          category: 'Database',
          icon: Database
        }
        
      ];

  const currentTab = tabs.find((tab) => tab.id === activeTab);
  const isTender = activeTab === 'tender';

  useEffect(() => {
    fetchDocuments();
  }, [activeTab]);

  useEffect(() => {
    if (!currentTab) return;

    setUploadForm(buildEmptyForm(currentTab.category));
    setSelectedFile(null);
    setUploadedFilePath(null);
    setSelectedThumbnail(null);
    setUploadedThumbnailPath(null);
    setError(null);
    setSuccess(null);
    setSearch('');

    const fileInput = window.document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';

    const thumbnailInput = window.document.getElementById('thumbnail-upload');
    if (thumbnailInput) thumbnailInput.value = '';
  }, [activeTab]);

  const safeJsonResponse = async (response, fallbackMessage) => {
    const text = await response.text();
    let data = {};

    try {
      data = text ? JSON.parse(text) : {};
    } catch (_) {
      throw new Error(`${fallbackMessage} (${response.status})`);
    }

    if (!response.ok || data.error) {
      throw new Error(data.message || fallbackMessage);
    }

    return data;
  };

  const getDocumentUrl = (filePath) => {
    if (!filePath) return '';

    const cleanPath = String(filePath)
      .trim()
      .replace(/\\/g, '/')
      .replace(/^\/+/, '');

    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
      return cleanPath;
    }

    if (cleanPath.startsWith('uploads/')) {
      return `https://crd-training.in/${cleanPath}`;
    }

    if (
      cleanPath.startsWith('docs/') ||
      cleanPath.startsWith('images/') ||
      cleanPath.startsWith('nature/') ||
      cleanPath.startsWith('tenders/')
    ) {
      return `https://crd-training.in/uploads/${cleanPath}`;
    }

    return `https://crd-training.in/uploads/docs/${cleanPath}`;
  };

  const handleView = (doc) => {
    if (!doc?.file) {
      setError('File path is missing for this document');
      return;
    }

    window.open(getDocumentUrl(doc.file), '_blank', 'noopener,noreferrer');
  };

  const handleDownload = (doc) => {
    if (!doc?.file) {
      setError('File path is missing for this document');
      return;
    }

    const link = window.document.createElement('a');
    link.href = getDocumentUrl(doc.file);
    link.download = doc.name?.toLowerCase().endsWith('.pdf')
      ? doc.name
      : `${doc.name || 'document'}.pdf`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${api.BASE_URL}/admin/uploads`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('authToken')
        }
      });

      if (response.status === 404) {
        setDocuments([]);
        return;
      }

      const text = await response.text();
      let data = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch (_) {
        throw new Error(`Failed to fetch documents (${response.status})`);
      }

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch documents');
      }

      // Existing getUploads currently returns error:true even for a successful list.
      // For this endpoint we use HTTP status + returned data array.
      setDocuments(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(`Error fetching documents: ${err.message}`);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select only image files for thumbnail');
      event.target.value = '';
      return;
    }

    setSelectedThumbnail(file);
    setError(null);
    setSuccess(null);

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${api.BASE_URL}/admin/upload`, {
        method: 'POST',
        headers: {
          Authorization: localStorage.getItem('authToken')
        },
        body: formData
      });

      const data = await safeJsonResponse(response, 'Thumbnail upload failed');
      setUploadedThumbnailPath(data.file);
      setSuccess('Thumbnail uploaded successfully.');
    } catch (err) {
      setError(`Thumbnail upload error: ${err.message}`);
      setSelectedThumbnail(null);
      setUploadedThumbnailPath(null);

      const thumbnailInput = window.document.getElementById('thumbnail-upload');
      if (thumbnailInput) thumbnailInput.value = '';
    } finally {
      setUploading(false);
    }
  };

  const removeThumbnail = () => {
    setSelectedThumbnail(null);
    setUploadedThumbnailPath(null);

    const thumbnailInput = window.document.getElementById('thumbnail-upload');
    if (thumbnailInput) thumbnailInput.value = '';
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please select only PDF files');
      event.target.value = '';
      return;
    }

    const documentName =
      uploadForm.name.trim() ||
      file.name.replace(/\.pdf$/i, '').trim();

    if (!uploadForm.name.trim()) {
      setUploadForm((prev) => ({
        ...prev,
        name: documentName
      }));
    }

    setSelectedFile(file);
    setError(null);
    setSuccess(null);

    await uploadFile(file, documentName);
  };

  const uploadFile = async (file, documentName) => {
    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();

      // IMPORTANT:
      // For Tender files, type must be added before file so Multer
      // can route it to /uploads/tenders/.
      if (isTender) {
        formData.append('type', 'tender');

        // Current backend middleware uses tender_no only for physical filename.
        // We no longer show Tender Number in UI, so use Document Name only
        // for the server-side filename. It is NOT saved in document metadata.
        formData.append('tender_no', documentName || 'TENDER');
      }

      formData.append('file', file);

      const response = await fetch(`${api.BASE_URL}/admin/upload`, {
        method: 'POST',
        headers: {
          Authorization: localStorage.getItem('authToken')
        },
        body: formData
      });

      const data = await safeJsonResponse(response, 'File upload failed');

      setUploadedFilePath(data.file);
      setSuccess(
        isTender
          ? 'Tender PDF uploaded successfully. Review and submit the document.'
          : 'File uploaded successfully. Review and submit the document.'
      );
    } catch (err) {
      setError(`File upload error: ${err.message}`);
      setSelectedFile(null);
      setUploadedFilePath(null);

      const fileInput = window.document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadedFilePath(null);

    const fileInput = window.document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const raw = String(dateString).slice(0, 10);
    const [year, month, day] = raw.split('-').map(Number);

    if (!year || !month || !day) return raw;

    return new Date(year, month - 1, day).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!uploadedFilePath) {
      setError('Please upload a PDF file first');
      return;
    }

    if (
      !uploadForm.name.trim() ||
      !uploadForm.category.trim() ||
      !uploadForm.date.trim()
    ) {
      setError('Please fill in all required fields');
      return;
    }

    if (
      userRole === 'state' &&
      (!uploadForm.year || !uploadedThumbnailPath)
    ) {
      setError('Please provide year and thumbnail for training modules');
      return;
    }

    const actionText = isTender
      ? 'submit this tender document'
      : 'submit this document';

    if (!window.confirm(`Are you sure you want to ${actionText}?`)) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        name: uploadForm.name.trim(),
        category: uploadForm.category.trim(),
        file: uploadedFilePath,
        date: uploadForm.date,
        ...(userRole === 'state' && {
          year: uploadForm.year,
          thumbnail: uploadedThumbnailPath
        })
      };

      const response = await fetch(`${api.BASE_URL}/admin/upload/detail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('authToken')
        },
        body: JSON.stringify(payload)
      });

      await safeJsonResponse(response, 'Failed to save document');

      setSuccess(
        isTender
          ? 'Tender document submitted successfully.'
          : 'Document submitted successfully.'
      );

      setUploadForm(buildEmptyForm(currentTab?.category || ''));
      setSelectedFile(null);
      setUploadedFilePath(null);
      setSelectedThumbnail(null);
      setUploadedThumbnailPath(null);

      const fileInput = window.document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';

      const thumbnailInput = window.document.getElementById('thumbnail-upload');
      if (thumbnailInput) thumbnailInput.value = '';

      await fetchDocuments();

      setTimeout(() => {
        setSuccess(null);
      }, 4000);
    } catch (err) {
      setError(`Submission error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (doc) => {
    const label =
      doc?.category === 'Tender Documents'
        ? 'tender document'
        : 'document';

    if (!window.confirm(`Are you sure you want to delete this ${label}?`)) {
      return;
    }

    try {
      setDeleting(doc.id);
      setError(null);

      const response = await fetch(`${api.BASE_URL}/admin/upload/${doc.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('authToken')
        },
        body: JSON.stringify({
          status: 'Delete'
        })
      });

      await safeJsonResponse(response, `Failed to delete ${label}`);

      setSuccess(
        doc?.category === 'Tender Documents'
          ? 'Tender document deleted successfully.'
          : 'Document deleted successfully.'
      );

      await fetchDocuments();
    } catch (err) {
      setError(`Delete error: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const filteredDocuments = useMemo(() => {
    if (!currentTab) return [];

    const query = search.trim().toLowerCase();

    return documents
      .filter((doc) => doc.category === currentTab.category)
      .filter((doc) => {
        if (!query) return true;

        return [doc.name, doc.category]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => {
        const dateA = String(a.date || '').slice(0, 10);
        const dateB = String(b.date || '').slice(0, 10);

        if (dateA !== dateB) {
          return dateB.localeCompare(dateA);
        }

        return Number(b.id || 0) - Number(a.id || 0);
      });
  }, [documents, currentTab, search]);

  const submitDisabled =
    submitting ||
    uploading ||
    !uploadedFilePath ||
    !uploadForm.name.trim() ||
    !uploadForm.category.trim() ||
    !uploadForm.date.trim() ||
    (
      userRole === 'state' &&
      (!uploadForm.year || !uploadedThumbnailPath)
    );

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8 py-6 lg:py-8">
      {/* Page Header */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 px-5 py-6 sm:px-7 sm:py-7 mb-6 shadow-sm">
        <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-blue-500/10" />
        <div className="absolute right-24 -bottom-20 h-44 w-44 rounded-full bg-cyan-400/10" />

        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-blue-100">
              <FolderOpen size={14} />
              CRD Document Centre
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
              Document Management
            </h1>

            <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-300">
              Upload and manage official CRD documents.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white">
              {isTender ? <Megaphone size={21} /> : <FileText size={21} />}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-slate-300">
                Current Section
              </p>
              <p className="font-semibold text-white">
                {currentTab?.label || 'Documents'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        <nav className="flex min-w-max gap-2" aria-label="Document categories">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                  active
                    ? tab.id === 'tender'
                      ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200 shadow-sm'
                      : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon size={17} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Upload Form */}
      <div
        className={`mb-7 overflow-hidden rounded-2xl border bg-white shadow-sm ${
          isTender ? 'border-amber-200' : 'border-slate-200'
        }`}
      >
        <div
          className={`border-b px-5 py-4 sm:px-6 ${
            isTender
              ? 'border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50'
              : 'border-slate-200 bg-slate-50/70'
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl ${
                isTender
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {isTender ? <Megaphone size={20} /> : <Upload size={20} />}
            </div>

            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {isTender ? 'Upload Tender Document' : 'Upload New Document'}
              </h2>

              <p className="mt-1 text-sm text-slate-600">
                {isTender
                  ? 'Upload a tender document to the portal.'
                  : `Add a new ${currentTab?.label?.toLowerCase() || 'document'} to the portal.`}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6">
          {/* SAME FIELDS FOR TENDER AND NORMAL DOCUMENTS */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="Document Name" required>
              <input
                type="text"
                value={uploadForm.name}
                onChange={(e) =>
                  setUploadForm((prev) => ({
                    ...prev,
                    name: e.target.value
                  }))
                }
                className="form-input-modern"
                placeholder="Enter document name"
                required
              />
            </Field>

            <Field label="Category" required>
              <input
                type="text"
                value={uploadForm.category}
                readOnly
                className="form-input-modern bg-slate-50 text-slate-600 cursor-not-allowed"
              />
            </Field>

            <Field label="Date" required>
              <input
                type="date"
                value={uploadForm.date}
                onChange={(e) =>
                  setUploadForm((prev) => ({
                    ...prev,
                    date: e.target.value
                  }))
                }
                className="form-input-modern"
                required
              />
            </Field>

            {userRole === 'state' && (
              <Field label="Year" required>
                <input
                  type="text"
                  value={uploadForm.year}
                  onChange={(e) =>
                    setUploadForm((prev) => ({
                      ...prev,
                      year: e.target.value
                    }))
                  }
                  className="form-input-modern"
                  placeholder="e.g. 2026"
                  required
                />
              </Field>
            )}
          </div>

          {userRole === 'state' && (
            <div className="mt-6">
              <UploadBox
                id="thumbnail-upload"
                title="Thumbnail Image"
                note="PNG, JPG or GIF"
                accept="image/*"
                uploading={uploading}
                onChange={handleThumbnailSelect}
                selectedFile={selectedThumbnail}
                uploaded={uploadedThumbnailPath}
                onRemove={removeThumbnail}
                icon={FileText}
                formatFileSize={formatFileSize}
              />
            </div>
          )}

          <div className="mt-6">
            <UploadBox
              id="file-upload"
              title="PDF Document"
              note="PDF up to 50MB"
              accept=".pdf,application/pdf"
              uploading={uploading}
              onChange={handleFileSelect}
              selectedFile={selectedFile}
              uploaded={uploadedFilePath}
              onRemove={removeFile}
              icon={Upload}
              formatFileSize={formatFileSize}
              accent={isTender ? 'amber' : 'blue'}
            />
          </div>

          {(error || success) && (
            <div className="mt-5">
              {error && (
                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                  {success}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end border-t border-slate-100 pt-5">
            <button
              type="submit"
              disabled={submitDisabled}
              className={`inline-flex min-w-[180px] items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white shadow-sm transition-all ${
                submitDisabled
                  ? 'cursor-not-allowed bg-slate-300'
                  : isTender
                    ? 'bg-amber-600 hover:bg-amber-700 focus:ring-4 focus:ring-amber-100'
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-100'
              }`}
            >
              {submitting ? (
                <Spinner />
              ) : isTender ? (
                <Megaphone size={18} />
              ) : (
                <Upload size={18} />
              )}

              {submitting
                ? 'Submitting...'
                : isTender
                  ? 'Submit Tender'
                  : 'Submit Document'}
            </button>
          </div>
        </form>
      </div>

      {/* Uploaded Documents */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-5 py-4 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {currentTab?.label}
              <span className="ml-1 text-sm font-medium text-slate-400">
                ({filteredDocuments.length})
              </span>
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Review, open, download or remove uploaded records.
            </p>
          </div>

          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                isTender
                  ? 'Search tender documents...'
                  : 'Search documents...'
              }
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Spinner dark />
            <p className="mt-3 text-sm">Loading documents...</p>
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <TableHead>Document</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead align="right">Actions</TableHead>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredDocuments.map((doc) => (
                  <tr
                    key={doc.id}
                    className="transition-colors hover:bg-slate-50/80"
                  >
                    <td className="px-5 py-4 sm:px-6">
                      <div className="flex min-w-[240px] items-start gap-3">
                        <div
                          className={`mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
                            isTender
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          {isTender ? (
                            <Megaphone size={19} />
                          ) : (
                            <FileText size={19} />
                          )}
                        </div>

                        <div>
                          <p className="font-semibold text-slate-900">
                            {doc.name}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-400">
                            ID: {doc.id}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="whitespace-nowrap px-5 py-4 sm:px-6">
                      <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <Calendar size={15} className="text-slate-400" />
                        {formatDate(doc.date)}
                      </div>
                    </td>

                    <td className="whitespace-nowrap px-5 py-4 sm:px-6">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                          isTender
                            ? 'bg-amber-50 text-amber-700 ring-amber-200'
                            : 'bg-blue-50 text-blue-700 ring-blue-200'
                        }`}
                      >
                        {doc.category}
                      </span>
                    </td>

                    <td className="whitespace-nowrap px-5 py-4 text-right sm:px-6">
                      <div className="flex items-center justify-end gap-1.5">
                        <ActionButton
                          title="View"
                          onClick={() => handleView(doc)}
                          tone="blue"
                        >
                          <Eye size={16} />
                        </ActionButton>

                        <ActionButton
                          title="Download"
                          onClick={() => handleDownload(doc)}
                          tone="green"
                        >
                          <Download size={16} />
                        </ActionButton>

                        <ActionButton
                          title="Delete"
                          onClick={() => handleDelete(doc)}
                          tone="red"
                          disabled={deleting === doc.id}
                        >
                          {deleting === doc.id ? (
                            <Spinner small dark />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              {isTender ? (
                <Megaphone size={26} />
              ) : (
                <FileText size={26} />
              )}
            </div>

            <h3 className="font-semibold text-slate-800">
              No {currentTab?.label?.toLowerCase()} found
            </h3>

            <p className="mt-1 max-w-sm text-sm text-slate-500">
              {search
                ? 'Try a different search term.'
                : 'Upload your first document using the form above.'}
            </p>
          </div>
        )}
      </div>

      <style>{`
        .form-input-modern {
          width: 100%;
          border: 1px solid rgb(203 213 225);
          border-radius: 0.75rem;
          padding: 0.7rem 0.85rem;
          font-size: 0.875rem;
          color: rgb(15 23 42);
          outline: none;
          transition: 150ms ease;
          background-color: white;
        }

        .form-input-modern:focus {
          border-color: rgb(59 130 246);
          box-shadow: 0 0 0 4px rgb(239 246 255);
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="mb-2 block text-sm font-semibold text-slate-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>

    {children}

    {hint && (
      <p className="mt-1.5 text-xs text-slate-500">
        {hint}
      </p>
    )}
  </div>
);

const UploadBox = ({
  id,
  title,
  note,
  accept,
  uploading,
  onChange,
  selectedFile,
  uploaded,
  onRemove,
  icon: Icon,
  formatFileSize,
  accent = 'blue'
}) => {
  const accentClass =
    accent === 'amber'
      ? 'border-amber-200 bg-amber-50/40 hover:border-amber-400'
      : 'border-slate-300 bg-slate-50/50 hover:border-blue-400';

  const iconClass =
    accent === 'amber'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-blue-100 text-blue-700';

  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {title}
        <span className="ml-1 text-red-500">*</span>
      </label>

      <label
        htmlFor={id}
        className={`block cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition ${accentClass}`}
      >
        <div
          className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl ${iconClass}`}
        >
          <Icon size={22} />
        </div>

        <p className="text-sm font-semibold text-slate-800">
          Choose a file
          <span className="font-normal text-slate-500">
            {' '}or click to browse
          </span>
        </p>

        <p className="mt-1 text-xs text-slate-500">
          {note}
        </p>

        <input
          id={id}
          type="file"
          className="sr-only"
          accept={accept}
          onChange={onChange}
          disabled={uploading}
        />
      </label>

      {selectedFile && (
        <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div
              className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                uploaded
                  ? 'bg-emerald-50 text-emerald-600'
                  : 'bg-blue-50 text-blue-600'
              }`}
            >
              <FileText size={18} />
            </div>

            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">
                {selectedFile.name}
              </p>

              <p className="text-xs text-slate-500">
                {formatFileSize(selectedFile.size)}
                {uploaded
                  ? ' • Uploaded successfully'
                  : uploading
                    ? ' • Uploading...'
                    : ''}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onRemove}
            disabled={uploading}
            className="ml-3 rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
          >
            <X size={17} />
          </button>
        </div>
      )}
    </div>
  );
};

const TableHead = ({ children, align = 'left' }) => (
  <th
    className={`px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 sm:px-6 ${
      align === 'right' ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
);

const ActionButton = ({
  children,
  title,
  onClick,
  tone,
  disabled
}) => {
  const tones = {
    blue: 'text-blue-600 hover:bg-blue-50 hover:text-blue-800',
    green: 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800',
    red: 'text-red-600 hover:bg-red-50 hover:text-red-800'
  };

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg p-2 transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.blue
      }`}
    >
      {children}
    </button>
  );
};

const Spinner = ({ small = false, dark = false }) => (
  <svg
    className={`animate-spin ${
      small ? 'h-4 w-4' : 'h-5 w-5'
    } ${
      dark ? 'text-slate-500' : 'text-white'
    }`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default Dossiers;
